/**
 * Workers React Query Hooks
 * Provides data fetching hooks for worker management components
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProviderUnified';
import * as workersApi from '../services/api/workers';
import logger from '../utils/logger';

// Query keys
export const WORKERS_KEYS = {
  all: ['workers'],
  list: (params) => [...WORKERS_KEYS.all, 'list', params],
  detail: (id) => [...WORKERS_KEYS.all, 'detail', id],
  summary: () => [...WORKERS_KEYS.all, 'summary'],
  certifications: (id) => [...WORKERS_KEYS.all, 'certifications', id],
  myCertifications: (userId) => [...WORKERS_KEYS.all, 'me', 'certifications', userId],
  certificationsSummary: () => [...WORKERS_KEYS.all, 'certifications-summary'],
  activity: (id, params) => [...WORKERS_KEYS.all, 'activity', id, params],
  jobsiteWorkers: (jobsiteId) => [...WORKERS_KEYS.all, 'jobsite', jobsiteId],
  compliance: () => [...WORKERS_KEYS.all, 'compliance'],
  invites: () => [...WORKERS_KEYS.all, 'invites'],
};

/**
 * Stable per-user React Query cache identity from auth context.
 * Prefer firebaseUid: present as soon as the Firebase session exists (before
 * /api/auth/me hydrates Mongo _id) and does not flip when `uid` is overwritten
 * with the Mongo ObjectId. Fall back to uid / _id / id for JWT/legacy users.
 */
export function resolveMyCertificationsCacheUserId(user) {
  if (!user) return null;
  if (user.firebaseUid != null && user.firebaseUid !== '') return String(user.firebaseUid);
  if (user.uid != null && user.uid !== '') return String(user.uid);
  if (user._id != null && user._id !== '') return String(user._id);
  if (user.id != null && user.id !== '') return String(user.id);
  return null;
}

const normalizeId = (value) => (value == null ? null : String(value));

const getWorkerIds = (worker) => [
  worker?._id,
  worker?.id,
  worker?.userId?._id,
  worker?.userId?.id,
  worker?.user?._id,
  worker?.user?.id,
].map(normalizeId).filter(Boolean);

const getAssignmentFromResponse = (response, requestedJobsiteId) => {
  const payload = response?.data || response || {};
  const hasCurrentJobsite = Object.prototype.hasOwnProperty.call(payload, 'currentJobsite');
  const currentJobsite = hasCurrentJobsite ? payload.currentJobsite : undefined;
  const currentSiteId = hasCurrentJobsite
    ? currentJobsite?._id || currentJobsite?.id || null
    : requestedJobsiteId || null;

  return {
    workerId: payload.workerId || null,
    currentSiteId,
    currentSiteName: currentJobsite?.name || '',
    updatedAt: payload.updatedAt || null,
  };
};

const applyAssignmentToWorker = (worker, assignment) => {
  if (!worker) return worker;

  const matchIds = [assignment.workerId, assignment.targetWorkerId].map(normalizeId).filter(Boolean);
  if (!matchIds.some((id) => getWorkerIds(worker).includes(id))) {
    return worker;
  }

  const siteId = assignment.currentSiteId || null;
  const siteName = assignment.currentSiteName || '';
  const site = siteId ? { _id: siteId, id: siteId, name: siteName } : null;

  return {
    ...worker,
    primaryJobsite: site,
    jobsiteId: siteId,
    jobsiteName: siteName,
    currentSite: siteName || 'Unassigned',
    currentSiteId: siteId,
    currentJobsite: site,
    updatedAt: assignment.updatedAt || worker.updatedAt,
    userId: typeof worker.userId === 'object' && worker.userId !== null
      ? { ...worker.userId, currentJobsite: site }
      : worker.userId,
    user: typeof worker.user === 'object' && worker.user !== null
      ? { ...worker.user, currentJobsite: site }
      : worker.user,
  };
};

const applyAssignmentToWorkersCache = (cacheValue, targetWorkerId, assignment) => {
  if (!cacheValue) return cacheValue;
  const scopedAssignment = { ...assignment, targetWorkerId };

  if (Array.isArray(cacheValue)) {
    return cacheValue.map((worker) => applyAssignmentToWorker(worker, scopedAssignment));
  }

  if (Array.isArray(cacheValue.workers)) {
    return {
      ...cacheValue,
      workers: cacheValue.workers.map((worker) => applyAssignmentToWorker(worker, scopedAssignment)),
    };
  }

  if (Array.isArray(cacheValue.data?.workers)) {
    return {
      ...cacheValue,
      data: {
        ...cacheValue.data,
        workers: cacheValue.data.workers.map((worker) => applyAssignmentToWorker(worker, scopedAssignment)),
      },
    };
  }

  if (Array.isArray(cacheValue.data)) {
    return {
      ...cacheValue,
      data: cacheValue.data.map((worker) => applyAssignmentToWorker(worker, scopedAssignment)),
    };
  }

  return applyAssignmentToWorker(cacheValue, scopedAssignment);
};

/**
 * Hook to fetch all workers
 */
export function useWorkers(params = {}, options = {}) {
  return useQuery({
    queryKey: WORKERS_KEYS.list(params),
    queryFn: () => workersApi.fetchWorkers(params),
    staleTime: 60000,
    ...options,
  });
}

/**
 * Hook to fetch workers for a specific jobsite
 */
export function useJobsiteWorkers(jobsiteId, options = {}) {
  return useQuery({
    queryKey: WORKERS_KEYS.jobsiteWorkers(jobsiteId),
    queryFn: () => workersApi.fetchJobsiteWorkers(jobsiteId),
    enabled: !!jobsiteId,
    staleTime: 60000,
    ...options,
  });
}

/**
 * Hook to fetch a single worker
 */
export function useWorker(workerId, options = {}) {
  return useQuery({
    queryKey: WORKERS_KEYS.detail(workerId),
    queryFn: () => workersApi.fetchWorker(workerId),
    enabled: !!workerId,
    staleTime: 60000,
    ...options,
  });
}

/**
 * Hook to fetch worker summary for dashboard
 * Returns aggregated statistics: worker counts, role distribution,
 * jobsite distribution, recently added workers, and compliance stats
 */
export function useWorkerSummary(options = {}) {
  return useQuery({
    queryKey: WORKERS_KEYS.summary(),
    queryFn: workersApi.fetchWorkerSummary,
    staleTime: 60000, // 1 minute
    ...options,
  });
}

/**
 * Hook to fetch worker certifications
 */
export function useWorkerCertifications(workerId, options = {}) {
  return useQuery({
    queryKey: WORKERS_KEYS.certifications(workerId),
    queryFn: () => workersApi.fetchWorkerCertifications(workerId),
    enabled: !!workerId,
    staleTime: 60000,
    ...options,
  });
}

/**
 * Hook to fetch the authenticated worker's certifications.
 * GET /api/workers/me/certifications — independent of /api/auth/me hydration.
 * Query key is scoped to the authenticated user so a 24h persisted cache cannot
 * leak certification names/document URLs across sequential logins on a shared browser.
 */
export function useMyWorkerCertifications(options = {}) {
  const { user } = useAuth() || {};
  const cacheUserId = resolveMyCertificationsCacheUserId(user);
  return useQuery({
    staleTime: 60000,
    ...options,
    queryKey: WORKERS_KEYS.myCertifications(cacheUserId),
    queryFn: workersApi.fetchMyWorkerCertifications,
    enabled: !!cacheUserId && options.enabled !== false,
  });
}

/**
 * Hook to create a worker certification
 */
export function useCreateWorkerCertification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workerId, data }) => workersApi.createWorkerCertification(workerId, data),
    onSuccess: (_, { workerId }) => {
      queryClient.invalidateQueries({ queryKey: WORKERS_KEYS.certifications(workerId) });
      logger.info('[useWorkers] Worker certification created successfully');
    },
    onError: (error) => {
      logger.error('[useWorkers] Create worker certification failed', { error: error.message });
    },
  });
}

/**
 * Hook to fetch worker activity feed
 */
export function useWorkerActivity(workerId, params = {}, options = {}) {
  return useQuery({
    queryKey: WORKERS_KEYS.activity(workerId, params),
    queryFn: () => workersApi.fetchWorkerActivity(workerId, params),
    enabled: !!workerId,
    staleTime: 60000,
    ...options,
  });
}

/**
 * Hook to fetch organization certifications summary
 */
export function useCertificationsSummary(options = {}) {
  return useQuery({
    queryKey: WORKERS_KEYS.certificationsSummary(),
    queryFn: workersApi.fetchCertificationsSummary,
    staleTime: 300000,
    ...options,
  });
}

/**
 * Hook to fetch compliance summary
 */
export function useComplianceSummary(options = {}) {
  return useQuery({
    queryKey: WORKERS_KEYS.compliance(),
    queryFn: workersApi.fetchComplianceSummary,
    staleTime: 300000, // 5 minutes
    ...options,
  });
}

/**
 * Hook to invite a worker
 */
export function useInviteWorker() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => workersApi.inviteWorker(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WORKERS_KEYS.invites() });
      queryClient.invalidateQueries({ queryKey: WORKERS_KEYS.all });
      logger.info('[useWorkers] Worker invited successfully');
    },
    onError: (error) => {
      logger.error('[useWorkers] Invite worker failed', { error: error.message });
    },
  });
}

/**
 * Hook to fetch pending worker invites
 */
export function useWorkerInvites(options = {}) {
  return useQuery({
    queryKey: WORKERS_KEYS.invites(),
    queryFn: workersApi.fetchWorkerInvites,
    staleTime: 60000,
    ...options,
  });
}

/**
 * Hook to preview worker import
 */
export function usePreviewWorkerImport() {
  return useMutation({
    mutationFn: (file) => workersApi.previewWorkerImport(file),
  });
}

/**
 * Hook to import workers from CSV
 */
export function useImportWorkers() {
  return useMutation({
    mutationFn: (file) => workersApi.importWorkers(file),
  });
}

/**
 * Hook to update worker profile
 */
export function useUpdateWorker() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workerId, data }) => workersApi.updateWorker(workerId, data),
    onSuccess: (_, { workerId }) => {
      queryClient.invalidateQueries({ queryKey: WORKERS_KEYS.detail(workerId) });
      queryClient.invalidateQueries({ queryKey: WORKERS_KEYS.all });
      logger.info('[useWorkers] Worker profile updated successfully');
    },
    onError: (error) => {
      logger.error('[useWorkers] Update worker failed', { error: error.message });
    },
  });
}

/**
 * Hook to update worker assignment
 */
export function useUpdateWorkerAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workerId, data }) => workersApi.updateWorkerAssignment(workerId, data),
    onSuccess: (response, { workerId, data }) => {
      const assignment = getAssignmentFromResponse(response, data?.jobsiteId ?? null);
      queryClient.setQueriesData(
        { queryKey: WORKERS_KEYS.all },
        (cacheValue) => applyAssignmentToWorkersCache(cacheValue, workerId, assignment),
      );
      queryClient.setQueryData(
        WORKERS_KEYS.detail(workerId),
        (cacheValue) => applyAssignmentToWorkersCache(cacheValue, workerId, assignment),
      );
      queryClient.invalidateQueries({ queryKey: WORKERS_KEYS.detail(workerId) });
      queryClient.invalidateQueries({ queryKey: WORKERS_KEYS.all });
    },
  });
}

export default {
  useWorkers,
  useJobsiteWorkers,
  useWorker,
  useWorkerSummary,
  useWorkerCertifications,
  useMyWorkerCertifications,
  useCreateWorkerCertification,
  useWorkerActivity,
  useCertificationsSummary,
  useComplianceSummary,
  useInviteWorker,
  useWorkerInvites,
  usePreviewWorkerImport,
  useImportWorkers,
  useUpdateWorker,
  useUpdateWorkerAssignment,
};
